import copy from 'fast-copy';
import shortid from 'shortid';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import isFunction from 'lodash/isFunction';
import isUndefined from 'lodash/isUndefined';
import jsonPointer from 'json-pointer';
import isEqual from 'react-fast-compare';
import AbstractControl from './AbstractControl';
import TdsAdapter from '../TdsAdapter';
import dictToObject from '../utils/dictToObject';
import getCompiledSchema from '../utils/getCompiledSchema';
import getDict from '../utils/getDict';
import getFilteredFormData from '../utils/getFilteredFormData';
import mergeDeep from '../utils/mergeDeep';
import debounce from '../utils/debounce';
import getPointer from '../utils/getPointer';
import removeKeyFromObj from '../utils/removeKeyFromObj';
import diff from '../utils/diff';
import { JSON_SCHEMA_KEYWORDS } from '../constants';

// NOTE: This is a good candidate to offload to a web worker.
// Needs to be implemented at the consuming application
// Provide tools to support this.
const compileSchema = ({
  adapter,
  validator,
  schema = {},
  shouldUpdateValues = true,
  touchedValues,
  defaultValues,
  valuesDict,
}) => {
  const compiledSchema = getCompiledSchema({
    adapter,
    defaultValues,
    validator,
    schema,
    shouldUpdateValues,
    touchedValues,
    values: dictToObject(valuesDict),
  });

  return {
    defaultValues: compiledSchema.defaultValues,
    values: compiledSchema.values,
    schema: compiledSchema.schema,
    schemaMap: compiledSchema.schemaMap,
    validate:
      validator && isFunction(validator.compile)
        ? validator.compile(compiledSchema.schema)
        : () => true,
  };
};

const getData = (values = {}, removeEmptyValues = true) => {
  let filteredValues = values;

  if (removeEmptyValues) {
    filteredValues = getFilteredFormData(values);
  }

  return dictToObject(filteredValues);
};

export default class ContextControl extends AbstractControl {
  static defaultProps = {
    focusOnInvalid: true,
    removeAdditional: true,
    removeEmptyValues: true,
    validateOnBlur: true,
    validateOnChange: true,
  };

  adapter = new TdsAdapter();

  subscriptions = new Map([
    ['aftervalidate', new Set()],
    ['beforevalidate', new Set()],
    ['blur', new Set()],
    ['change', new Set()],
    ['focus', new Set()],
    ['error', new Set()],
    ['state', new Set()],
    ['schema', new Set()],
    ['touched', new Set()],
    ['register', new Set()],
    ['unregister', new Set()],
    ['relevant', new Set()],
  ]);

  uuid = shortid.generate();

  requestCompileSchema = debounce((schema) => this.compileSchema(schema ?? this.props.schema), 250);

  requestValidation = debounce(async () => this.validate(), 250);

  keepState = new Set();

  multistepFields = new Set();

  schemaMapCache = new Map();

  userSchemaMap = new Map();

  fieldRegister = new Map();

  fieldRefs = new Map();

  fieldRelevance = new Map();

  relevanceCallbacks = new Map();

  get values() {
    const { values } = this.state;

    return dictToObject(this.removeAdditional(values));
  }

  get defaultValues() {
    const { defaultValues } = this.state;

    return dictToObject(this.removeAdditional(defaultValues));
  }

  get initialValues() {
    const { defaultValues, initialValues } = this.state;

    return dictToObject(this.removeAdditional({ ...defaultValues, ...initialValues }));
  }

  get errors() {
    const { errors } = this.state;

    return dictToObject(this.removeAdditional(errors));
  }

  constructor(props, stateRef, formStateRef) {
    super(props);

    this.stateRef = stateRef;
    this.formStateRef = formStateRef;

    this.setDefaults();

    const { components } = props;

    if (components) {
      Object.entries(components).forEach(([component, options]) => {
        this.adapter.register(component, options);
      });
    }

    this.subscribe('change', this.handleValidationOnChange);
    this.subscribe('blur', this.handleValidationOnBlur);
  }

  setState(newState) {
    this.state = {
      ...this.state,
      ...newState,
    };

    let nextFormState = this.getState();
    const previousFieldRelevance = new Map(this.fieldRelevance);

    this.relevanceCallbacks.forEach((relevanceCallback, dataPath) => {
      this.setFieldRelevance(dataPath, relevanceCallback(nextFormState));
    });

    // re-fetch computed state when field relevance has changed
    const relevantFields = Object.entries(diff(previousFieldRelevance, this.fieldRelevance));
    if (relevantFields.length > 0) {
      const { touched: prevTouched, values: prevValues } = this.state;
      const values = { ...prevValues };
      const touched = new Set(prevTouched);

      relevantFields.forEach(([dataPath, isRelevant]) => {
        const keepState = this.keepState.has(dataPath);

        if (!isRelevant && !keepState) {
          values[dataPath] = this.getInitialValue(dataPath);

          touched.delete(dataPath);
        }
      });

      this.state = {
        ...this.state,
        touched,
        values,
      };

      nextFormState = this.getState();
    }
    this.stateRef.current = nextFormState;
    this.trigger('state', nextFormState);
  }

  setDefaults = () => {
    const { i18n = {}, schema, validator, initialValues: userInitialValues } = this.props;
    const { initialValues, defaultValues } = this.state ?? {};

    const valuesDict = initialValues ?? getDict(userInitialValues);
    const touchedValues = new Set();
    this.setState({
      $submitted: false,
      $submitting: false,
      errors: null,
      i18n,
      initialValues: valuesDict,
      touched: new Set(),
      touchedValues,
      ...compileSchema({
        adapter: this.adapter,
        defaultValues,
        schema: this.injectUserSchema(schema),
        touchedValues,
        validator,
        valuesDict,
      }),
    });
  };

  compileSchema = (schema) => {
    const { defaultValues, touchedValues, values: valuesDict } = this.state;
    const { validator } = this.props;

    this.setState(
      compileSchema({
        adapter: this.adapter,
        defaultValues,
        schema: this.injectUserSchema(schema),
        touchedValues,
        validator,
        valuesDict,
      })
    );
  };

  injectUserSchema(schema = {}) {
    let parsedSchema = copy(schema);
    const sortedUserSchemaMap = [...this.userSchemaMap].sort();

    for (const [$ref, userSchema] of sortedUserSchemaMap) {
      const currentSchema = getPointer(schema, $ref) ?? {};

      if ($ref) {
        jsonPointer.set(parsedSchema, $ref, { ...currentSchema, ...userSchema });
      } else {
        parsedSchema = { ...currentSchema, ...userSchema };
      }
    }

    return parsedSchema;
  }

  updateUserSchema = ($ref, schema) => {
    const { schemaMap } = this.state;

    if (schema == null && schemaMap.has($ref)) {
      this.userSchemaMap.delete($ref);

      return;
    }

    this.userSchemaMap.set($ref, schema ?? {});
    this.requestCompileSchema();
  };

  updateDefaultValue = (dataPath, value) => {
    const { defaultValues: prevDefaultValues, touchedValues, values: prevValues } = this.state;
    let defaultValues = { ...prevDefaultValues };
    let values = { ...prevValues };
    const valuesDict = getDict(value);

    if (valuesDict) {
      for (const fullDataPath of Object.keys(values)) {
        if (fullDataPath.match(new RegExp(`^${dataPath}`))) {
          defaultValues = removeKeyFromObj(defaultValues, fullDataPath);

          if (!touchedValues.has(fullDataPath)) {
            values = removeKeyFromObj(values, fullDataPath);
          }
        }
      }

      for (const subDataPath of Object.keys(valuesDict)) {
        const fullDataPath = `${dataPath}${subDataPath}`;

        defaultValues[fullDataPath] = valuesDict[subDataPath];

        if (!touchedValues.has(fullDataPath)) {
          values[fullDataPath] = this.getInitialValue(fullDataPath) ?? valuesDict[subDataPath];
        }
      }
    } else {
      defaultValues[dataPath] = value;

      if (!touchedValues.has(dataPath)) {
        values[dataPath] = this.getInitialValue(dataPath) ?? value;
      }
    }

    this.setState({
      defaultValues,
      values,
    });

    this.requestCompileSchema();
  };

  updateInitialValue = (dataPath, value) => {
    const { initialValues: prevInitialValues, touchedValues, values: prevValues } = this.state;
    let initialValues = { ...prevInitialValues };
    let values = { ...prevValues };
    const valuesDict = getDict(value);

    if (valuesDict) {
      for (const fullDataPath of Object.keys(values)) {
        if (fullDataPath.match(new RegExp(`^${dataPath}`))) {
          initialValues = removeKeyFromObj(initialValues, fullDataPath);

          if (!touchedValues.has(fullDataPath)) {
            values = removeKeyFromObj(values, fullDataPath);
          }
        }
      }

      for (const subDataPath of Object.keys(valuesDict)) {
        const fullDataPath = `${dataPath}${subDataPath}`;

        initialValues[fullDataPath] = valuesDict[subDataPath];

        if (!touchedValues.has(fullDataPath)) {
          values[fullDataPath] = valuesDict[subDataPath];
        }
      }
    } else {
      initialValues[dataPath] = value;

      if (!touchedValues.has(dataPath)) {
        values[dataPath] = value;
      }
    }

    this.setState({
      initialValues,
      values,
    });

    this.requestCompileSchema();
  };

  setCurrentTouched = () => {
    this.fieldRegister.forEach((formFieldControl) => {
      if (formFieldControl?.setTouched) {
        formFieldControl.setTouched();
      }
    });
  };

  setValues = (fn) => {
    const { touchedValues, values } = this.state;
    const updatedValues = fn(values);
    const updatedTouchedValues = new Set(touchedValues);

    if (isPlainObject(updatedValues)) {
      Object.keys(updatedValues).forEach((dataPath) => {
        if (!isEqual(values[dataPath], updatedValues[dataPath])) {
          updatedTouchedValues.add(dataPath);
        }
      });
    }

    this.setState({
      values: updatedValues,
      touchedValues: updatedTouchedValues,
    });
  };

  setErrors = (fn) => {
    const { errors } = this.state;

    this.setState({
      errors: fn(errors),
    });
  };

  clearErrors = () => {
    this.setState({
      errors: {},
    });
  };

  setSubmitting = (isSubmitting = true) => {
    this.fieldRegister.forEach((formFieldControl) => {
      formFieldControl.trigger('submit');
    });

    this.setState({
      $submitted: true,
      $submitting: isSubmitting,
    });
  };

  setInitialValues = (initialValues) => {
    const { touchedValues, values: currentValues } = this.state;
    const updatedInitialValues = getDict(initialValues);

    const updatedValues = Object.keys(updatedInitialValues)
      .filter((key) => !touchedValues.has(key))
      .reduce(
        (values, key) => ({
          ...values,
          [key]: updatedInitialValues[key],
        }),
        currentValues
      );

    this.setState({
      initialValues: updatedInitialValues,
      values: updatedValues,
    });
  };

  setTouched = (dataPath, isTouched = true) => {
    const { touched: prevTouched } = this.state;
    const touched = new Set(prevTouched);

    if (isTouched) {
      touched.add(dataPath);
    } else {
      touched.delete(dataPath);
    }

    this.setState({
      touched,
    });
  };

  setFieldRelevance = (dataPath, relevant = true) => {
    this.fieldRelevance.set(dataPath, relevant);
  };

  setRelevanceCallback = (dataPath, callback) => {
    this.relevanceCallbacks.set(dataPath, callback);
  };

  getAdapter = () => this.adapter;

  getError = (dataPath) => {
    if (dataPath === '') {
      const { errors } = this.state;

      return errors?.[''];
    }

    return getPointer(this.errors, dataPath);
  };

  getDefaultValue = (dataPath) => {
    if (dataPath === '') {
      const { defaultValues } = this.state;
      const { type: fieldType } = this.getRefFromSchema(dataPath);

      return fieldType === 'object' ? this.defaultValues : defaultValues[''];
    }

    return getPointer(this.defaultValues, dataPath);
  };

  getInitialValue = (dataPath) => {
    if (dataPath === '') {
      const { initialValues } = this.state;
      const { type: fieldType } = this.getRefFromSchema(dataPath);

      return fieldType === 'object' ? this.initialValues : initialValues[''];
    }

    return getPointer(this.initialValues, dataPath);
  };

  getValue = (dataPath) => {
    if (dataPath === '') {
      const { values } = this.state;
      const { type: fieldType } = this.getRefFromSchema(dataPath);

      return fieldType === 'object' ? this.values : values[''];
    }

    return getPointer(this.values, dataPath);
  };

  getRefFromSchema = ($ref) => {
    const { schemaMap } = this.state;
    const cleanRef = isString($ref) ? $ref.replace(/^#?(.*)/, '$1') : null;

    if (schemaMap.has(cleanRef)) {
      this.schemaMapCache.set(cleanRef, schemaMap.get(cleanRef));
    }

    return this.schemaMapCache.get(cleanRef) ?? {};
  };

  register = (dataPath, props) => {
    const {
      defaultValues: prevDefaultValues,
      initialValues: prevInitialValues,
      touchedValues,
      values: prevValues,
    } = this.state;

    const {
      $ref,
      defaultValue,
      initialValue,
      isMultistepField = false,
      keepState = false,
      formFieldControl,
      schema: userSchema = {},
      ...schemaProps
    } = props;
    const defaultValues = { ...prevDefaultValues };
    const initialValues = { ...prevInitialValues };
    const values = { ...prevValues };

    this.fieldRegister.set(dataPath, formFieldControl);

    if (!isUndefined(defaultValue)) {
      defaultValues[dataPath] = defaultValue;
    }

    if (!isUndefined(initialValue)) {
      initialValues[dataPath] = initialValue;
    }

    if (!touchedValues.has(dataPath)) {
      values[dataPath] = [initialValues[dataPath], defaultValues[dataPath]].find(
        (value) => !isUndefined(value)
      );
    }

    if (keepState) {
      this.keepState.add(dataPath);
    }

    if (isMultistepField) {
      this.multistepFields.add(dataPath);
    } else {
      // clean up
      this.multistepFields.delete(dataPath);
    }

    JSON_SCHEMA_KEYWORDS.forEach((keyword) => {
      if (!isUndefined(schemaProps[keyword])) {
        userSchema[keyword.replace('-', ':')] = schemaProps[keyword];
      }
    });

    this.updateUserSchema($ref, userSchema);

    this.setState({
      defaultValues,
      initialValues,
      values,
    });

    this.trigger('register', dataPath, this.fieldRegister);
  };

  unregister = (dataPath, props) => {
    const { $ref, keepState = false } = props;
    const { touched: prevTouched, values } = this.state;
    const nextValues = { ...values };
    const touched = new Set(prevTouched);

    this.fieldRegister.delete(dataPath);
    this.userSchemaMap.delete($ref);

    if (!keepState) {
      this.keepState.delete(dataPath);
    }

    const isMultistepField = this.multistepFields.has(dataPath);

    if (!keepState && !isMultistepField) {
      touched.delete(dataPath);

      nextValues[dataPath] = this.getInitialValue(dataPath);
    }

    // trigger state change as values could have changed
    this.setState({
      touched,
      values: nextValues,
    });

    this.trigger('unregister', dataPath, this.fieldRegister);
  };

  registerFieldRef = (dataPath, ref) => {
    this.fieldRefs.set(dataPath, ref);
  };

  getFieldRef = (dataPath) => {
    return this.fieldRefs.get(dataPath);
  };

  getField = (dataPath) => {
    return this.fieldRegister.get(dataPath);
  };

  reset = () => {
    this.setDefaults();
  };

  resetField = (dataPath) => {
    const { schema, validator } = this.props;
    const {
      defaultValues,
      errors,
      initialValues,
      touched,
      touchedValues,
      values: valuesDict,
    } = this.state;
    const nextTouched = new Set(touched);
    const nextTouchedValues = new Set(touchedValues);
    const nextValuesDict = removeKeyFromObj(valuesDict, dataPath);

    nextValuesDict[dataPath] = [initialValues[dataPath], defaultValues[dataPath]].find(
      (value) => !isUndefined(value)
    );

    nextTouched.delete(dataPath);
    nextTouchedValues.delete(dataPath);

    this.setState({
      errors: removeKeyFromObj(errors ?? {}, dataPath),
      touched: nextTouched,
      touchedValues: nextTouchedValues,
      ...compileSchema({
        adapter: this.adapter,
        defaultValues,
        schema: this.injectUserSchema(schema),
        touchedValues: nextTouchedValues,
        validator,
        valuesDict: nextValuesDict,
      }),
    });
  };

  removeAdditional(data) {
    const { removeAdditional } = this.props;

    if (removeAdditional) {
      const cleanData = {};

      for (const [dataPath, value] of Object.entries(data ?? {})) {
        const hasField = this.fieldRegister.has(dataPath);
        const isFieldRelevant = this.isFieldRelevant(dataPath);
        const hasFieldKeptState = this.keepState.has(dataPath);
        const isMultistepField = this.multistepFields.has(dataPath);

        if (isFieldRelevant && (hasField || hasFieldKeptState || isMultistepField)) {
          cleanData[dataPath] = value;
        }
      }

      return cleanData;
    }

    return data;
  }

  isFieldRelevant = (dataPath) => {
    return this.fieldRelevance.get(dataPath);
  };

  async onAfterValidate(data, errors) {
    const response = await this.triggerAsync('aftervalidate', data, errors);

    return mergeDeep({ data, errors }, ...response.map(([output] = []) => output), {
      concatArray: false,
    });
  }

  async onBeforeValidate(data) {
    const response = await this.triggerAsync('beforevalidate', data);

    return mergeDeep({ data }, ...response.map(([output] = []) => output), { concatArray: false });
  }

  validate = async () => {
    const { noValidate, removeEmptyValues, validator } = this.props;
    const { validate, values } = this.state;
    let data = getData(values, removeEmptyValues);
    let errors = {};

    if (noValidate === true) {
      return data;
    }

    ({ data = data, errors = errors } = await this.onBeforeValidate(data));

    if (validator && isFunction(validator.onValidate)) {
      ({ data = data, errors = errors } = await validator.onValidate(data, errors, validate));
    }

    ({ data = data, errors = errors } = await this.onAfterValidate(data, errors));

    if (Object.keys(errors).length > 0) {
      this.setErrors(() => errors);

      return false;
    }

    this.clearErrors();

    return data;
  };

  getState = () => {
    const { errors, validate, values, ...state } = this.state;

    return {
      ...state,
      defaultValues: this.defaultValues,
      errors: this.errors,
      errorsDict: errors ?? {},
      fieldRegister: this.fieldRegister,
      fieldRelevance: this.fieldRelevance,
      initialValues: this.initialValues,
      uuid: this.uuid,
      values: this.values,
      valuesDict: values,
    };
  };

  handleValidationOnBlur = () => {
    const { validateOnBlur } = this.props;

    if (validateOnBlur) {
      this.requestValidation();
    }
  };

  handleValidationOnChange = () => {
    const { validateOnChange } = this.props;

    if (validateOnChange) {
      this.requestValidation();
    }
  };

  focusFirstError = () => {
    const { focusOnInvalid } = this.props;

    if (focusOnInvalid) {
      const { errors } = this.state;

      for (const dataPath of this.fieldRegister.keys()) {
        const fieldRef = this.getFieldRef(dataPath);

        if (!isUndefined(errors[dataPath]) && fieldRef?.current) {
          if (typeof fieldRef.current.focus === 'function') {
            fieldRef.current.focus();
          }

          break;
        }
      }
    }
  };
}
